import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import './LicenseAssignment.css';
import './RecoverDeletedUsers.css';

/* ─── Types ──────────────────────────────────────────────── */

type RunStatus =
  | 'queued'
  | 'resolving'
  | 'awaiting_confirmation'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

type ItemStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'partial'
  | 'error'
  | 'skipped';

interface RecoveryRun {
  id: string;
  status: RunStatus;
  triggered_by: string | null;
  input_text: string;
  domains_requested: number;
  domains_eligible: number;
  domains_eligible_warn_14d: number;
  domains_skipped_not_found: number;
  domains_skipped_outside_window: number;
  domains_skipped_not_eligible: number;
  domains_skipped_already_restored: number;
  domains_completed: number;
  domains_partial: number;
  domains_error: number;
  users_undeleted_total: number;
  dkim_activated_total: number;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  confirmed_at: string | null;
  finished_at: string | null;
}

interface RecoveryRunItem {
  id: string;
  run_id: string;
  domain_name: string;
  panel_email: string | null;
  panel_source: 'panels' | 'inventory_panels' | null;
  cf_account_name: string | null;
  cf_zone_id: string | null;
  removal_finished_at: string | null;
  age_days: number | null;
  status: ItemStatus;
  error_code: string | null;
  error_message: string | null;
  warn_code: string | null;
  domain_added_at: string | null;
  domain_add_result: string | null;
  domain_verified_at: string | null;
  cf_verification_action: string | null;
  users_undeleted_count: number;
  users_already_active_count: number;
  users_undeleted_at: string | null;
  dkim_activated_at: string | null;
  dkim_status: string | null;
  cf_dkim_action: string | null;
  started_at: string | null;
  finished_at: string | null;
}

interface RunDetail {
  run: RecoveryRun;
  items: RecoveryRunItem[];
}

interface PreviewResponse {
  run: RecoveryRun;
  eligible: RecoveryRunItem[];
  eligibleWarn14d: RecoveryRunItem[];
  skipped: {
    not_found: RecoveryRunItem[];
    outside_window: RecoveryRunItem[];
    not_eligible: RecoveryRunItem[];
    already_restored: RecoveryRunItem[];
  };
  errored: RecoveryRunItem[];
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

/* ─── Constants ──────────────────────────────────────────── */

const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

const INFLIGHT_STATUSES: ReadonlySet<RunStatus> = new Set([
  'queued',
  'resolving',
  'awaiting_confirmation',
  'running',
]);

const INITIAL_VISIBLE = 15;
const PAGE_INCREMENT = 40;
const SERVER_PAGE = 50;

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function durationLabel(start: string | null, end: string | null): string {
  if (!start) return '-';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  return restSec === 0 ? `${min}m` : `${min}m ${restSec}s`;
}

function statusBadgeClass(status: RunStatus): string {
  switch (status) {
    case 'queued':
      return 'lic-badge--queued';
    case 'resolving':
    case 'running':
      return 'lic-badge--running';
    case 'awaiting_confirmation':
      return 'lic-badge--warn';
    case 'completed':
      return 'lic-badge--completed';
    case 'failed':
      return 'lic-badge--failed';
    case 'cancelled':
      return 'lic-badge--skipped';
    default:
      return 'lic-badge--pending';
  }
}

function itemBadgeClass(status: ItemStatus): string {
  switch (status) {
    case 'pending':
      return 'lic-badge--pending';
    case 'running':
      return 'lic-badge--running';
    case 'completed':
      return 'lic-badge--success';
    case 'partial':
      return 'lic-badge--partial';
    case 'error':
      return 'lic-badge--error';
    case 'skipped':
      return 'lic-badge--skipped';
    default:
      return 'lic-badge--pending';
  }
}

const PlusIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const DownloadIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/* ─── View modes ─────────────────────────────────────────── */

type View =
  | { mode: 'list' }
  | { mode: 'wizard' }
  | { mode: 'detail'; runId: string };

/* ─── Main component ─────────────────────────────────────── */

export function RecoverDeletedUsers(): JSX.Element {
  const [view, setView] = useState<View>({ mode: 'list' });
  const [runs, setRuns] = useState<RecoveryRun[] | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderExhausted, setOlderExhausted] = useState(false);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refreshList = useCallback(async (): Promise<void> => {
    try {
      const data = await api<RecoveryRun[]>('/api/recovery/runs');
      setRuns((prev) => {
        if (!prev) return data;
        const headIds = new Set(data.map((r) => r.id));
        const older = prev.filter((r) => !headIds.has(r.id));
        return [...data, ...older];
      });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, [showToast]);

  const loadOlder = useCallback(async (): Promise<void> => {
    if (!runs || runs.length === 0 || loadingOlder || olderExhausted) return;
    setLoadingOlder(true);
    try {
      const oldest = runs[runs.length - 1];
      const data = await api<RecoveryRun[]>(
        `/api/recovery/runs?before=${encodeURIComponent(oldest.created_at)}&limit=${SERVER_PAGE}`,
      );
      if (data.length === 0) {
        setOlderExhausted(true);
      } else {
        setRuns((prev) => [...(prev ?? []), ...data]);
        setVisibleCount((c) => c + PAGE_INCREMENT);
      }
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load older runs');
    } finally {
      setLoadingOlder(false);
    }
  }, [runs, loadingOlder, olderExhausted, showToast]);

  const showMore = useCallback((): void => {
    if (!runs) return;
    if (visibleCount < runs.length) {
      setVisibleCount((c) => Math.min(c + PAGE_INCREMENT, runs.length));
    } else {
      void loadOlder();
    }
  }, [runs, visibleCount, loadOlder]);

  /* List polling: 3s while inflight, 7s otherwise. */
  useEffect(() => {
    if (view.mode === 'list') void refreshList();
  }, [view.mode, refreshList]);

  const listPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (view.mode !== 'list') {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
      return;
    }
    const hasInflight = runs?.some((r) => INFLIGHT_STATUSES.has(r.status));
    const intervalMs = hasInflight ? 3000 : 7000;
    if (listPollIntervalRef.current !== null) {
      window.clearInterval(listPollIntervalRef.current);
    }
    listPollIntervalRef.current = window.setInterval(() => {
      void refreshList();
    }, intervalMs);
    return () => {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
    };
  }, [view.mode, runs, refreshList]);

  /* Render: detail / wizard / list */

  if (view.mode === 'detail') {
    return (
      <RunDetailView
        runId={view.runId}
        onBack={() => {
          setView({ mode: 'list' });
          void refreshList();
        }}
        onError={(msg) => showToast('error', msg)}
        showToast={showToast}
        toast={toast}
      />
    );
  }

  if (view.mode === 'wizard') {
    return (
      <NewRunWizard
        onCancelToList={() => {
          setView({ mode: 'list' });
          void refreshList();
        }}
        onOpenDetail={(runId) => setView({ mode: 'detail', runId })}
        showToast={showToast}
        toast={toast}
      />
    );
  }

  return (
    <RunsListView
      runs={runs}
      visibleCount={visibleCount}
      loadingOlder={loadingOlder}
      olderExhausted={olderExhausted}
      onShowMore={showMore}
      onNew={() => setView({ mode: 'wizard' })}
      onOpen={(runId) => setView({ mode: 'detail', runId })}
      onRefresh={() => void refreshList()}
      toast={toast}
    />
  );
}

/* ─── State 1: List view ─────────────────────────────────── */

function RunsListView({
  runs,
  visibleCount,
  loadingOlder,
  olderExhausted,
  onShowMore,
  onNew,
  onOpen,
  onRefresh,
  toast,
}: {
  runs: RecoveryRun[] | null;
  visibleCount: number;
  loadingOlder: boolean;
  olderExhausted: boolean;
  onShowMore: () => void;
  onNew: () => void;
  onOpen: (runId: string) => void;
  onRefresh: () => void;
  toast: Toast | null;
}): JSX.Element {
  const totalRuns = runs?.length ?? 0;
  const visibleRuns = runs ? runs.slice(0, visibleCount) : null;
  const inflight = runs?.find((r) => INFLIGHT_STATUSES.has(r.status)) ?? null;
  const canShowMore = runs !== null && (visibleCount < runs.length || !olderExhausted);

  return (
    <div className="lic-root">
      <div className="lic-scroll">
        <header className="lic-header">
          <div className="lic-header-row">
            <div>
              <h1 className="lic-title">Recover Deleted Users</h1>
              <p className="lic-subtitle">
                Restore domains and undelete user mailboxes within Google's 20-day retention
                window. Paste a list of domain names; the wizard validates eligibility and
                shows a preview before any destructive work runs.
              </p>
            </div>
            <button
              type="button"
              className="lic-btn lic-btn--primary"
              onClick={onNew}
              disabled={inflight !== null}
              title={
                inflight
                  ? 'A recovery run is already in progress'
                  : 'Start a new recovery run'
              }
            >
              <PlusIcon />
              {inflight ? 'Run in progress...' : 'New Recovery Run'}
            </button>
          </div>
        </header>

        {inflight && (
          <section className="rdu-snapshot-card">
            <div className="rdu-snapshot-meta">
              An active recovery run is in flight ({inflight.status}). Click the row below to
              view its progress.
            </div>
          </section>
        )}

        <section className="lic-card">
          <div className="lic-card-header">
            <h2 className="lic-card-title">
              Runs{' '}
              <span className="lic-cell-faint">
                ({Math.min(visibleCount, totalRuns)}
                {totalRuns > 0 && (
                  <>
                    {' '}of {totalRuns}
                    {!olderExhausted && '+'}
                  </>
                )}
                )
              </span>
            </h2>
            <button
              type="button"
              className="lic-icon-btn"
              onClick={onRefresh}
              title="Refresh list"
            >
              <RefreshIcon />
            </button>
          </div>

          {runs === null && <div className="lic-empty">Loading...</div>}

          {runs !== null && runs.length === 0 && (
            <div className="lic-empty">
              <div className="lic-empty-icon">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 7 8 12 3 17" />
                  <path d="M21 17a4 4 0 0 0-4-4H8" />
                </svg>
              </div>
              <h3 className="lic-empty-title">No runs yet</h3>
              <p className="lic-empty-text">
                Click <strong>New Recovery Run</strong> above to start one. You'll paste a list
                of domains, the wizard checks eligibility against the 20-day undelete window,
                and you confirm before anything mutates.
              </p>
            </div>
          )}

          {visibleRuns !== null && visibleRuns.length > 0 && (
            <table className="lic-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Status</th>
                  <th className="lic-col-num">Domains</th>
                  <th className="lic-col-num">Recovered</th>
                  <th className="lic-col-num">Failed</th>
                  <th>By</th>
                  <th className="lic-col-date">Started</th>
                  <th className="lic-col-date">Duration</th>
                </tr>
              </thead>
              <tbody>
                {visibleRuns.map((r) => (
                  <tr key={r.id} className="lic-row" onClick={() => onOpen(r.id)}>
                    <td className="lic-cell-mono lic-cell-faint">{r.id.slice(0, 8)}</td>
                    <td>
                      <span className={`lic-badge ${statusBadgeClass(r.status)}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--ink">{r.domains_requested}</span>
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--sage">
                        {r.domains_completed || '-'}
                      </span>
                    </td>
                    <td className="lic-col-num">
                      <span className="lic-num lic-num--rose">
                        {r.domains_error || '-'}
                      </span>
                    </td>
                    <td className="lic-cell-mono lic-cell-faint">
                      {r.triggered_by ?? '-'}
                    </td>
                    <td className="lic-col-date lic-cell-faint">
                      {relativeTime(r.started_at ?? r.created_at)}
                    </td>
                    <td className="lic-col-date lic-cell-faint">
                      {durationLabel(r.started_at, r.finished_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {visibleRuns !== null && visibleRuns.length > 0 && canShowMore && (
            <div className="lic-loadmore-row">
              <button
                type="button"
                className="lic-btn lic-btn--ghost"
                onClick={onShowMore}
                disabled={loadingOlder}
              >
                {loadingOlder ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}
          {visibleRuns !== null && visibleRuns.length > 0 && !canShowMore && (
            <div className="lic-loadmore-row lic-cell-faint">All runs loaded.</div>
          )}
        </section>
      </div>
      {toast && <div className={`lic-toast lic-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── State 2: New-run wizard ────────────────────────────── */

type WizardStep = 'paste' | 'resolving' | 'preview';

function NewRunWizard({
  onCancelToList,
  onOpenDetail,
  showToast,
  toast,
}: {
  onCancelToList: () => void;
  onOpenDetail: (runId: string) => void;
  showToast: (type: Toast['type'], message: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const [step, setStep] = useState<WizardStep>('paste');
  const [inputText, setInputText] = useState('');
  const [starting, setStarting] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<RecoveryRun | null>(null);
  const [items, setItems] = useState<RecoveryRunItem[]>([]);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  /* Submit input: POST /recovery/runs */
  const handleStart = async (): Promise<void> => {
    const text = inputText.trim();
    if (text.length === 0) {
      showToast('error', 'Paste at least one domain.');
      return;
    }
    setStarting(true);
    try {
      const result = await api<{ runId: string; domainsRequested: number }>(
        '/api/recovery/runs',
        { method: 'POST', body: JSON.stringify({ inputText: text }) },
      );
      setRunId(result.runId);
      setStep('resolving');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
        const existingRunId = (err.data as { existingRunId?: string } | undefined)?.existingRunId;
        if (existingRunId) {
          showToast('success', 'A recovery run is already in progress - opening it now.');
          onOpenDetail(existingRunId);
          return;
        }
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to start run';
      showToast('error', msg);
    } finally {
      setStarting(false);
    }
  };

  /* Resolving step polling: poll the run + items at 1.5s. */
  const resolvePollRef = useRef<number | null>(null);
  useEffect(() => {
    if (step !== 'resolving' || !runId) {
      if (resolvePollRef.current !== null) {
        window.clearInterval(resolvePollRef.current);
        resolvePollRef.current = null;
      }
      return;
    }

    let stopped = false;

    const poll = async (): Promise<void> => {
      try {
        const detail = await api<RunDetail>(`/api/recovery/runs/${runId}`);
        if (stopped) return;
        setRun(detail.run);
        setItems(detail.items);

        if (detail.run.status === 'awaiting_confirmation') {
          // Fetch preview and advance.
          try {
            const prev = await api<PreviewResponse>(
              `/api/recovery/runs/${runId}/preview`,
            );
            if (!stopped) {
              setPreview(prev);
              setStep('preview');
            }
          } catch (err) {
            if (!stopped) {
              showToast('error', err instanceof Error ? err.message : 'Failed to load preview');
            }
          }
          return;
        }

        if (
          detail.run.status === 'failed' ||
          detail.run.status === 'cancelled' ||
          detail.run.status === 'running' ||
          detail.run.status === 'completed'
        ) {
          if (!stopped) onOpenDetail(runId);
        }
      } catch (err) {
        if (!stopped) {
          showToast('error', err instanceof Error ? err.message : 'Failed to poll run');
        }
      }
    };

    void poll();
    resolvePollRef.current = window.setInterval(() => {
      void poll();
    }, 1500);

    return () => {
      stopped = true;
      if (resolvePollRef.current !== null) {
        window.clearInterval(resolvePollRef.current);
        resolvePollRef.current = null;
      }
    };
  }, [step, runId, showToast, onOpenDetail]);

  const handleConfirm = async (): Promise<void> => {
    if (!runId || !preview) return;
    if (typedConfirmation !== 'RECOVER') return;
    if (preview.eligible.length === 0) return;
    setConfirming(true);
    try {
      await api<{ runId: string; shards: number; items: number }>(
        `/api/recovery/runs/${runId}/confirm`,
        { method: 'POST', body: JSON.stringify({ typedConfirmation: 'RECOVER' }) },
      );
      onOpenDetail(runId);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to confirm run';
      showToast('error', msg);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (!runId) {
      onCancelToList();
      return;
    }
    setCancelling(true);
    try {
      await api<{ runId: string; status: 'cancelled' }>(
        `/api/recovery/runs/${runId}/cancel`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      onCancelToList();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to cancel run';
      showToast('error', msg);
    } finally {
      setCancelling(false);
    }
  };

  /* Resolving step progress */
  const domainsRequested = run?.domains_requested ?? 0;
  const domainsResolved = items.filter(
    (it) => it.status !== 'pending' || it.error_code !== null,
  ).length;
  const resolvePct =
    domainsRequested > 0
      ? Math.min(100, Math.round((domainsResolved / domainsRequested) * 100))
      : 0;

  /* Preview totals (counts + a single eligible-count for the CTA) */
  const previewEligibleCount =
    (preview?.eligible.length ?? 0) + (preview?.eligibleWarn14d.length ?? 0);

  return (
    <div className="lic-root">
      <div className="lic-scroll">
        <header className="lic-header">
          <button
            type="button"
            className="lic-back"
            onClick={() => {
              if (runId && step !== 'paste') {
                void handleCancel();
              } else {
                onCancelToList();
              }
            }}
          >
            <ChevronIcon />
            <span>Back to runs</span>
          </button>
          <div style={{ marginTop: 16 }}>
            <h1 className="lic-title">New Recovery Run</h1>
            <p className="lic-subtitle">
              Paste domain names (one per line). The wizard checks each domain against the
              Domain Removal history, validates the 20-day undelete window, and shows a
              classified preview before anything mutates.
            </p>
          </div>
        </header>

        <div className="rdu-wizard-container">
          {/* Step 1: paste textarea */}
          <section className="rdu-wizard-step">
            <div className="rdu-wizard-step-header">Step 1 - Paste domains</div>
            <textarea
              className="rdu-textarea"
              placeholder={'example.com\nanother-domain.com\n# lines starting with # are ignored'}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={step !== 'paste' || starting}
            />
            <div className="rdu-wizard-row" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="lic-btn lic-btn--primary"
                onClick={handleStart}
                disabled={step !== 'paste' || starting || inputText.trim().length === 0}
              >
                {starting ? 'Starting...' : 'Start'}
              </button>
              <button
                type="button"
                className="lic-btn lic-btn--ghost"
                onClick={onCancelToList}
                disabled={starting}
              >
                Back
              </button>
            </div>
          </section>

          {/* Step 2: resolving progress */}
          {(step === 'resolving' || step === 'preview') && (
            <section className="rdu-wizard-step">
              <div className="rdu-wizard-step-header">
                Step 2 - Resolving eligibility
              </div>
              <div
                className="rdu-stage-row"
                style={{ gridTemplateColumns: '1fr auto auto', borderBottom: 'none' }}
              >
                <div className="rdu-stage-progress">
                  <div
                    className="rdu-stage-progress-bar"
                    style={{ width: `${resolvePct}%` }}
                  />
                </div>
                <div className="rdu-stage-elapsed">
                  {domainsResolved}/{domainsRequested} resolved
                </div>
                <div className="rdu-stage-elapsed">
                  {run ? durationLabel(run.created_at, run.finished_at) : '-'}
                </div>
              </div>
              {step === 'resolving' && (
                <div className="rdu-stage-name" style={{ marginTop: 8 }}>
                  Resolving {domainsRequested} domain{domainsRequested === 1 ? '' : 's'}...
                </div>
              )}
              {step === 'preview' && (
                <div className="rdu-stage-name" style={{ marginTop: 8 }}>
                  Resolution complete - ready to preview eligibility.
                </div>
              )}
            </section>
          )}

          {/* Step 3: Preview + typed confirmation */}
          {step === 'preview' && preview && (
            <section className="rdu-wizard-step">
              <div className="rdu-wizard-step-header">Step 3 - Preview &amp; confirm</div>

              <PreviewSection
                title={`Will recover (${preview.eligible.length})`}
                titleClass="rdu-preview-list-title--sage"
                explainer="Eligible domains within Google's 20-day undelete window. These will be processed end-to-end (domain insert -> verify -> undelete users -> DKIM)."
                entries={preview.eligible}
                showAge
              />

              <PreviewSection
                title={`Within 14d window - flagged (${preview.eligibleWarn14d.length})`}
                titleClass="rdu-preview-list-title--honey"
                explainer="Eligible but close to Google's 20-day cliff. Will still run; the warning surfaces the time pressure so you can prioritize."
                entries={preview.eligibleWarn14d}
                showAge
                emphasizeAge
              />

              <PreviewSection
                title={`Outside window (${preview.skipped.outside_window.length})`}
                titleClass="rdu-preview-list-title--rose"
                explainer="Past Google's 20-day undelete retention cliff. Skipped - no recovery is possible."
                entries={preview.skipped.outside_window}
                showAge
              />

              <PreviewSection
                title={`Not eligible (${preview.skipped.not_eligible.length})`}
                titleClass="rdu-preview-list-title--rose"
                explainer="Removed via a process other than Domain Removal. Skipped - we cannot infer the original panel ownership."
                entries={preview.skipped.not_eligible}
                showAge={false}
              />

              <PreviewSection
                title={`Not found (${preview.skipped.not_found.length})`}
                titleClass="rdu-preview-list-title--muted"
                explainer="No Domain Removal record exists for this domain. Skipped - nothing to recover from."
                entries={preview.skipped.not_found}
                showAge={false}
              />

              <PreviewSection
                title={`Already restored (${preview.skipped.already_restored.length})`}
                titleClass="rdu-preview-list-title--muted"
                explainer="Domain already exists on the customer and is verified. Skipped - no work to do."
                entries={preview.skipped.already_restored}
                showAge={false}
              />

              {preview.errored.length > 0 && (
                <PreviewSection
                  title={`Errors - operator action required (${preview.errored.length})`}
                  titleClass="rdu-preview-list-title--rose"
                  explainer="Resolution failed for these domains (panel creds missing, CF zone not found, etc.). Fix the underlying issue and retry the run."
                  entries={preview.errored}
                  showAge={false}
                  showErrorCode
                />
              )}

              <div
                className="rdu-wizard-row"
                style={{ marginTop: 24, alignItems: 'center' }}
              >
                <input
                  type="text"
                  className="rdu-confirm-input"
                  placeholder="Type RECOVER"
                  value={typedConfirmation}
                  onChange={(e) => setTypedConfirmation(e.target.value)}
                  disabled={confirming || cancelling}
                />
                <button
                  type="button"
                  className="lic-btn lic-btn--primary"
                  onClick={handleConfirm}
                  disabled={
                    typedConfirmation !== 'RECOVER' ||
                    confirming ||
                    cancelling ||
                    previewEligibleCount === 0
                  }
                  title={
                    previewEligibleCount === 0
                      ? 'Nothing eligible to recover - cancel and try a different list'
                      : 'Run the recovery'
                  }
                >
                  {confirming ? 'Confirming...' : `Recover ${previewEligibleCount}`}
                </button>
                <button
                  type="button"
                  className="lic-btn lic-btn--ghost"
                  onClick={handleCancel}
                  disabled={confirming || cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
      {toast && <div className={`lic-toast lic-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function PreviewSection({
  title,
  titleClass,
  explainer,
  entries,
  showAge,
  emphasizeAge,
  showErrorCode,
}: {
  title: string;
  titleClass: string;
  explainer: string;
  entries: RecoveryRunItem[];
  showAge: boolean;
  emphasizeAge?: boolean;
  showErrorCode?: boolean;
}): JSX.Element {
  return (
    <div className="rdu-preview-list">
      <div className={`rdu-preview-list-title ${titleClass}`}>{title}</div>
      <p className="rdu-preview-explainer">{explainer}</p>
      {entries.length === 0 ? (
        <div className="lic-cell-faint" style={{ fontSize: 13 }}>(none)</div>
      ) : (
        <table className="lic-table">
          <tbody>
            {entries.map((e) => (
              <tr key={e.id}>
                <td className="lic-cell-mono">{e.domain_name}</td>
                <td className="lic-cell-mono lic-cell-faint">
                  {e.panel_email ?? '-'}
                  {e.cf_account_name ? ` · ${e.cf_account_name}` : ''}
                </td>
                {showAge && (
                  <td className="lic-col-num lic-cell-faint">
                    {e.age_days !== null ? (
                      emphasizeAge ? (
                        <strong>{e.age_days}d</strong>
                      ) : (
                        <>{e.age_days}d</>
                      )
                    ) : (
                      '-'
                    )}
                  </td>
                )}
                {showErrorCode && (
                  <td className="lic-cell-faint">
                    {e.error_code && (
                      <span className="rdu-error-tag">{e.error_code}</span>
                    )}
                    {e.error_message && (
                      <span className="rdu-error-msg">{e.error_message}</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── State 3: Run detail ────────────────────────────────── */

function RunDetailView({
  runId,
  onBack,
  onError,
  showToast,
  toast,
}: {
  runId: string;
  onBack: () => void;
  onError: (msg: string) => void;
  showToast: (type: Toast['type'], message: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [retrying, setRetrying] = useState(false);

  const refreshDetail = useCallback(async (): Promise<void> => {
    try {
      const data = await api<RunDetail>(`/api/recovery/runs/${runId}`);
      setDetail(data);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load run');
    }
  }, [runId, onError]);

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  /* Detail polling: 2.5s until terminal status. */
  const detailPollRef = useRef<number | null>(null);
  useEffect(() => {
    detailPollRef.current = window.setInterval(() => {
      void refreshDetail();
    }, 2500);
    return () => {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    };
  }, [refreshDetail]);

  useEffect(() => {
    if (!detail) return;
    if (TERMINAL_STATUSES.has(detail.run.status)) {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    }
  }, [detail]);

  const handleRetry = async (): Promise<void> => {
    setRetrying(true);
    try {
      await api<{ runId: string; retried: number; shards: number }>(
        `/api/recovery/runs/${runId}/retry-errors`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      showToast('success', 'Re-queued failed items - polling will resume.');
      await refreshDetail();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to retry errors';
      showToast('error', msg);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="lic-root">
      <div className="lic-scroll">
        <header className="lic-header">
          <button type="button" className="lic-back" onClick={onBack}>
            <ChevronIcon />
            <span>Back to runs</span>
          </button>
        </header>
        {detail ? (
          <RunDetailBody
            detail={detail}
            onRefresh={() => void refreshDetail()}
            onRetry={handleRetry}
            retrying={retrying}
          />
        ) : (
          <div className="lic-loading">Loading run...</div>
        )}
      </div>
      {toast && <div className={`lic-toast lic-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function RunDetailBody({
  detail,
  onRefresh,
  onRetry,
  retrying,
}: {
  detail: RunDetail;
  onRefresh: () => void;
  onRetry: () => void;
  retrying: boolean;
}): JSX.Element {
  const { run, items } = detail;

  // Item sort order: bad rows first (error / partial), pending second, completed/skipped last.
  const sortRank = (s: ItemStatus): number => {
    if (s === 'error' || s === 'partial') return 0;
    if (s === 'pending' || s === 'running') return 1;
    return 2;
  };
  const sortedItems = [...items].sort(
    (a, b) => sortRank(a.status) - sortRank(b.status),
  );

  const isTerminal = TERMINAL_STATUSES.has(run.status);
  const errorCount = run.domains_error;
  const skippedCount =
    run.domains_skipped_not_found +
    run.domains_skipped_outside_window +
    run.domains_skipped_not_eligible +
    run.domains_skipped_already_restored;

  return (
    <>
      <section className="lic-summary-card">
        <div className="lic-summary-top">
          <div>
            <h2 className="lic-summary-title">
              Run{' '}
              <span className="lic-cell-mono lic-cell-faint">{run.id.slice(0, 8)}</span>
            </h2>
            <div className="lic-summary-meta">
              {run.triggered_by ? (
                <>
                  Triggered by <strong>{run.triggered_by}</strong> ·{' '}
                </>
              ) : null}
              {relativeTime(run.created_at)}
              {run.started_at && (
                <> · ran {durationLabel(run.started_at, run.finished_at)}</>
              )}
            </div>
          </div>
          <div className="lic-summary-actions">
            <span className={`lic-badge ${statusBadgeClass(run.status)}`}>
              {run.status}
            </span>
            {isTerminal && errorCount > 0 && (
              <button
                type="button"
                className="lic-btn lic-btn--primary"
                onClick={onRetry}
                disabled={retrying}
                title="Re-queue failed items as pending and re-shard"
              >
                {retrying ? 'Retrying...' : `Retry ${errorCount} failed`}
              </button>
            )}
            {isTerminal && (
              <a
                className="lic-btn lic-btn--ghost"
                href={`/api/recovery/runs/${run.id}/csv`}
                download
              >
                <DownloadIcon />
                CSV
              </a>
            )}
            <button
              type="button"
              className="lic-icon-btn"
              onClick={onRefresh}
              title="Refresh"
            >
              <RefreshIcon />
            </button>
          </div>
        </div>

        {run.error_message && <div className="lic-summary-error">{run.error_message}</div>}

        <div className="lic-summary-grid">
          <SummaryStat label="Eligible" value={run.domains_eligible} variant="ink" />
          <SummaryStat
            label="Recovered"
            value={run.domains_completed}
            variant="sage"
          />
          <SummaryStat
            label="Partial"
            value={run.domains_partial}
            variant={run.domains_partial > 0 ? 'honey' : 'muted'}
          />
          <SummaryStat
            label="Failed"
            value={run.domains_error}
            variant={run.domains_error > 0 ? 'rose' : 'muted'}
          />
          <SummaryStat label="Skipped" value={skippedCount} variant="muted" />
          <SummaryStat
            label="Users undeleted"
            value={run.users_undeleted_total}
            variant="sage"
          />
          <SummaryStat
            label="DKIM activated"
            value={run.dkim_activated_total}
            variant="sage"
          />
        </div>
      </section>

      {/* Per-stage progress strip */}
      <StageStrip run={run} items={items} />

      <section className="lic-card">
        <div className="lic-card-header">
          <h2 className="lic-card-title">
            Domain results ({items.length})
            {!isTerminal && <span className="lic-inflight-pulse" />}
          </h2>
        </div>

        {items.length === 0 && (
          <div className="lic-empty">
            <span>No items in this run yet.</span>
          </div>
        )}

        {items.length > 0 && (
          <table className="lic-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Panel</th>
                <th>Status</th>
                <th className="lic-col-num">Age</th>
                <th>Phases</th>
                <th className="lic-col-num">Users</th>
                <th>DKIM</th>
                <th>Notes / error</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => {
                const isBad = it.status === 'error' || it.status === 'partial';
                const isWarn = it.warn_code === 'within_14d_warn' && !isBad;
                const rowClass = isBad
                  ? 'lic-row--bad'
                  : isWarn
                    ? 'rdu-row--warn'
                    : undefined;
                const phasesDone =
                  (it.domain_added_at ? 1 : 0) +
                  (it.domain_verified_at ? 1 : 0) +
                  (it.users_undeleted_at ? 1 : 0) +
                  (it.dkim_activated_at ? 1 : 0);
                return (
                  <tr key={it.id} className={rowClass}>
                    <td className="lic-cell-mono">{it.domain_name}</td>
                    <td className="lic-cell-mono lic-cell-faint">
                      {it.panel_email ?? '-'}
                    </td>
                    <td>
                      <span className={`lic-badge ${itemBadgeClass(it.status)}`}>
                        {it.status}
                      </span>
                    </td>
                    <td className="lic-col-num lic-cell-faint">
                      {it.age_days !== null ? `${it.age_days}d` : '-'}
                    </td>
                    <td className="lic-cell-faint">
                      <span className="lic-cell-mono">{phasesDone}/4</span>
                    </td>
                    <td className="lic-col-num">
                      {it.users_undeleted_count > 0 ? (
                        <span className="lic-num lic-num--sage">
                          {it.users_undeleted_count}
                        </span>
                      ) : (
                        <span className="lic-cell-faint">-</span>
                      )}
                      {it.users_already_active_count > 0 && (
                        <span className="lic-num-detail">
                          · {it.users_already_active_count} active
                        </span>
                      )}
                    </td>
                    <td className="lic-cell-faint">
                      {it.dkim_status ?? '-'}
                    </td>
                    <td className="lic-cell-faint lic-cell-detail">
                      {it.error_code && (
                        <span className="rdu-error-tag">{it.error_code}</span>
                      )}
                      {it.error_message && (
                        <span className="rdu-error-msg">{it.error_message}</span>
                      )}
                      {it.warn_code === 'within_14d_warn' && !it.error_code && (
                        <span className="rdu-error-msg" style={{ color: 'var(--honey)' }}>
                          Within 14d warning window.
                        </span>
                      )}
                      {isBad && (
                        <div className="lic-cta-resolve">
                          {it.error_code === 'panel_creds_missing'
                            ? 'Resolve: ensure panel credentials exist for this domain (panels or inventory_panels).'
                            : it.error_code === 'cf_zone_missing'
                              ? 'Resolve: add this domain to a Cloudflare account so the verify/DKIM TXT records can be written.'
                              : it.error_message ||
                                'Resolve this issue then click Retry failed.'}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/* ─── Stage strip helper ─────────────────────────────────── */

type StageState = 'pending' | 'running' | 'completed' | 'failed';

interface StageRow {
  key: 'p1' | 'p2' | 'p3' | 'p4';
  name: string;
  state: StageState;
  progress?: { done: number; total: number };
}

function deriveStages(run: RecoveryRun, items: RecoveryRunItem[]): StageRow[] {
  // Eligible = items that should run. Excludes skipped.
  const eligibleItems = items.filter((it) => it.status !== 'skipped');
  const total = Math.max(1, eligibleItems.length);

  // Per-phase done = items that have actually advanced past each phase
  // (timestamp populated) plus terminal items that bailed earlier (so the
  // running counter doesn't sit at the wrong total once errors land).
  const p1Done = eligibleItems.filter(
    (it) =>
      it.domain_added_at !== null ||
      it.status === 'completed' ||
      it.status === 'partial' ||
      it.status === 'error',
  ).length;
  const p2Done = eligibleItems.filter(
    (it) => it.domain_verified_at !== null || it.status === 'completed' || it.status === 'partial',
  ).length;
  const p3Done = eligibleItems.filter(
    (it) => it.users_undeleted_at !== null || it.status === 'completed' || it.status === 'partial',
  ).length;
  const p4Done = eligibleItems.filter(
    (it) => it.dkim_activated_at !== null || it.status === 'completed',
  ).length;

  const status = run.status;
  const isRunning = status === 'running';
  const isTerminal = status === 'completed' || status === 'failed' || status === 'cancelled';

  function stateFor(done: number): StageState {
    if (status === 'failed' && done === 0) return 'failed';
    if (done >= total) return 'completed';
    if (isRunning && done > 0) return 'running';
    if (isRunning) return 'running';
    if (isTerminal) return done > 0 ? 'completed' : 'pending';
    return 'pending';
  }

  return [
    {
      key: 'p1',
      name: 'Phase 1 - Domain insert',
      state: stateFor(p1Done),
      progress: { done: p1Done, total },
    },
    {
      key: 'p2',
      name: 'Phase 2 - Verify',
      state: stateFor(p2Done),
      progress: { done: p2Done, total },
    },
    {
      key: 'p3',
      name: 'Phase 3 - Undelete users',
      state: stateFor(p3Done),
      progress: { done: p3Done, total },
    },
    {
      key: 'p4',
      name: 'Phase 4 - DKIM',
      state: stateFor(p4Done),
      progress: { done: p4Done, total },
    },
  ];
}

function StageStrip({
  run,
  items,
}: {
  run: RecoveryRun;
  items: RecoveryRunItem[];
}): JSX.Element {
  const stages = deriveStages(run, items);

  return (
    <section className="rdu-stage-strip">
      {stages.map((s) => {
        const pct =
          s.progress && s.progress.total > 0
            ? Math.min(100, Math.round((s.progress.done / s.progress.total) * 100))
            : 0;
        return (
          <div key={s.key} className="rdu-stage-row">
            <span className="rdu-stage-name">{s.name}</span>
            <div>
              {s.state === 'running' && s.progress ? (
                <div className="rdu-stage-progress">
                  <div
                    className="rdu-stage-progress-bar"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              ) : (
                <span style={{ display: 'inline-block', width: 200 }} />
              )}
            </div>
            <span className={`rdu-stage-state rdu-stage-state--${s.state}`}>
              {s.progress
                ? `${s.progress.done}/${s.progress.total}`
                : s.state}
            </span>
            <span className="rdu-stage-elapsed">{s.state}</span>
          </div>
        );
      })}
    </section>
  );
}

/* ─── SummaryStat (inline-copied from LicenseAssignment) ── */

function SummaryStat({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted' | 'ink';
  hint?: string;
}): JSX.Element {
  const variantClass = variant === 'ink' ? 'default' : variant;
  return (
    <div className="lic-summary-stat">
      <span className="lic-summary-stat-label">{label}</span>
      <span className={`lic-summary-stat-value lic-summary-stat-value--${variantClass}`}>
        {value}
      </span>
      {hint && <span className="lic-summary-stat-hint">{hint}</span>}
    </div>
  );
}
